import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useError } from '../contexts/ErrorContext';
import { extractErrorMessage } from '../utils/errorHandling';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../api/client';

interface HostInfo {
  hostId: string;
  hostPublicKey: string;
  hostEndpoint: string;
  hostDisplayName: string;
  shareCode: string;
  isActive: boolean;
  // Whether the host offers "home" (split-tunnel) mode. The home option is only
  // shown when this is true. (#909)
  supportsHomeMode?: boolean;
}

type TunnelScope = 'full' | 'home';

interface WireGuardConfig {
  configString: string;
  friendIP: string;
  friendPublicKey: string;
}

const Join: React.FC = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { addError } = useError();
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);
  const [config, setConfig] = useState<WireGuardConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tunnelScope, setTunnelScope] = useState<TunnelScope>('full');

  const fetchHostInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/vpn-signaling/share-code/${shareCode}`);
      setHostInfo(response.data);
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to find host');
      addError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [addError, shareCode]);

  useEffect(() => {
    if (!shareCode) {
      addError('Invalid share code');
      setLoading(false);
      return;
    }

    fetchHostInfo();
  }, [addError, fetchHostInfo, shareCode]);

  const generateConfig = async () => {
    if (!hostInfo || !user) return;

    try {
      setGenerating(true);

      // Generate WireGuard config through backend
      const configResponse = await api.post('/api/v1/vpn-signaling/generate-config', {
        hostId: hostInfo.hostId,
        hostPublicKey: hostInfo.hostPublicKey,
        hostEndpoint: hostInfo.hostEndpoint,
        shareCode: shareCode,
        // Only send 'home' when the host actually offers it. (#909)
        tunnelScope: hostInfo.supportsHomeMode ? tunnelScope : 'full',
      });

      setConfig(configResponse.data);
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to generate config');
      addError(apiError.message);
    } finally {
      setGenerating(false);
    }
  };

  const downloadConfig = () => {
    if (!config) return;

    const blob = new Blob([config.configString], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${shareCode}-wireguard.conf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    if (!config) return;

    navigator.clipboard.writeText(config.configString);
    toast.success('Config copied! 📋 Paste it into WireGuard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <LoadingSkeleton type="card" count={2} />
        </div>
      </div>
    );
  }

  if (!hostInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="text-red-500 text-4xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                VPN Connection Not Available
              </h2>
              <p className="text-gray-600 mb-6">
                This share code is no longer valid or the host is offline.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Login Required
              </h2>
              <p className="text-gray-600 mb-6">
                You need to be logged in to connect to {hostInfo.hostDisplayName}'s VPN.
              </p>
              <button
                onClick={() => navigate('/login', { state: { returnTo: `/join/${shareCode}` }})}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Login to Connect
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center mb-6 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Connect to VPN
          </h1>
          <p className="mt-2 text-gray-600">
            {hostInfo.hostDisplayName} is sharing their connection with you
          </p>
        </div>

        {/* Host Info Card */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {hostInfo.hostDisplayName.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                {hostInfo.hostDisplayName}
              </h3>
              <p className="text-sm text-gray-500">
                Share Code: <span className="font-mono font-medium">{shareCode}</span>
              </p>
              <p className="text-sm text-gray-500">
                Endpoint: <span className="font-mono">{hostInfo.hostEndpoint}</span>
              </p>
            </div>
            <div className="ml-auto">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-1.5"></div>
                Online
              </span>
            </div>
          </div>
        </div>

        {/* Connection Details */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Connection Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Connection Type:</span>
              <span className="ml-2 text-gray-600">Direct P2P WireGuard</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Encryption:</span>
              <span className="ml-2 text-gray-600">ChaCha20Poly1305</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Traffic Routing:</span>
              <span className="ml-2 text-gray-600">
                {tunnelScope === 'home'
                  ? `Only ${hostInfo.hostDisplayName}'s home network`
                  : 'All traffic through host'}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Server Cost:</span>
              <span className="ml-2 text-green-600 font-medium">Zero - Direct P2P</span>
            </div>
          </div>
        </div>

        {!config ? (
          /* Generate Config Section */
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Get WireGuard Configuration
            </h3>
            <p className="text-gray-600 mb-6">
              Choose how you want to connect, then generate your WireGuard
              configuration for a direct, encrypted connection to {hostInfo.hostDisplayName}'s computer.
            </p>

            {/* Connection mode (#909). "Reach their home network" only appears
                when the host advertises home subnets. */}
            <fieldset className="mb-6 space-y-3">
              <legend className="text-sm font-medium text-gray-900 mb-2">Connection mode</legend>
              <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${tunnelScope === 'full' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="tunnelScope"
                  value="full"
                  checked={tunnelScope === 'full'}
                  onChange={() => setTunnelScope('full')}
                  className="mt-1 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="block font-medium text-gray-900">Use their internet</span>
                  <span className="block text-sm text-gray-600">
                    All your traffic goes out through {hostInfo.hostDisplayName}'s connection — you'll appear at their location.
                  </span>
                </span>
              </label>

              {hostInfo.supportsHomeMode && (
                <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${tunnelScope === 'home' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="tunnelScope"
                    value="home"
                    checked={tunnelScope === 'home'}
                    onChange={() => setTunnelScope('home')}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <span className="block font-medium text-gray-900">Reach their home network</span>
                    <span className="block text-sm text-gray-600">
                      Get to devices on {hostInfo.hostDisplayName}'s home network (NAS, printer, home server). Your own browsing stays normal.
                    </span>
                  </span>
                </label>
              )}
            </fieldset>

            <button
              onClick={generateConfig}
              disabled={generating}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating Configuration...
                </div>
              ) : (
                'Generate WireGuard Config'
              )}
            </button>
          </div>
        ) : (
          /* Config Ready Section */
          <div className="space-y-6">
            {/* Config Display */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Your WireGuard Configuration
              </h3>
              <div className="bg-gray-900 rounded-lg p-4 mb-4">
                <pre className="text-sm overflow-x-auto whitespace-pre-wrap font-mono">
                  {config.configString.split('\n').map((line, index) => {
                    // Section headers like [Interface], [Peer]
                    if (line.trim().startsWith('[') && line.trim().endsWith(']')) {
                      return <div key={index} className="text-purple-400 font-bold">{line}</div>;
                    }
                    // Key-value pairs
                    const [key, ...valueParts] = line.split('=');
                    if (valueParts.length > 0) {
                      return (
                        <div key={index}>
                          <span className="text-blue-400">{key}</span>
                          <span className="text-gray-500">=</span>
                          <span className="text-green-400">{valueParts.join('=')}</span>
                        </div>
                      );
                    }
                    // Empty lines or comments
                    return <div key={index} className="text-gray-600">{line}</div>;
                  })}
                </pre>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={downloadConfig}
                  className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Download Config File
                </button>
                <button
                  onClick={copyToClipboard}
                  className="flex-1 flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>

            {/* Setup Instructions */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Setup Instructions
              </h3>
              <div className="space-y-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-medium text-gray-900">Install WireGuard</h4>
                    <p className="text-gray-600 text-sm">
                      Download and install WireGuard from{' '}
                      <a 
                        href="https://www.wireguard.com/install/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-800"
                      >
                        wireguard.com/install
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-medium text-gray-900">Import Configuration</h4>
                    <p className="text-gray-600 text-sm">
                      Import the downloaded .conf file into WireGuard or copy/paste the config text
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-medium text-gray-900">Connect</h4>
                    <p className="text-gray-600 text-sm">
                      Activate the tunnel in WireGuard to connect directly to {hostInfo.hostDisplayName}'s {tunnelScope === 'home' ? 'home network' : 'internet'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-green-900 mb-4">
                ✅ Direct P2P Connection Benefits
              </h3>
              <ul className="space-y-2 text-sm text-green-800">
                <li>• Direct connection to {hostInfo.hostDisplayName}'s computer</li>
                <li>• No data goes through Fawayo servers</li>
                {tunnelScope === 'home' ? (
                  <li>• Reach {hostInfo.hostDisplayName}'s home devices; your own browsing stays normal</li>
                ) : (
                  <li>• Full speed of {hostInfo.hostDisplayName}'s internet connection</li>
                )}
                <li>• Works behind NAT with automatic setup</li>
                <li>• Military-grade WireGuard encryption</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Join;
